import {
  Button,
  ButtonColor,
  ButtonSize,
  Checkbox,
  Combobox,
  Option,
  TextInput,
} from '@code/innovance-react-ui-kit';
import React, { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';
import { Controller, FieldError, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { Customer, Fias } from 'src/front/api/apiInstance';
import {
  CardSelectionType,
  FiasAddressSuggestion,
  ReferenceDataType,
} from 'src/front/api/generated';
import useDebounce from 'src/front/common/hooks/useDebounce';
import usePRM, { useConditionalPRM } from 'src/front/common/hooks/usePRM';

import useYupResolver from 'src/front/common/hooks/useYupResolver';
import {
  formatAlphaNumericCyrillicPlusDashAndBlank,
  formatFreeText,
} from 'src/front/common/utils/formUtils';
import { AddressSchema } from 'src/front/common/utils/validationSchemas';
import Datepicker from 'src/front/components//Datepicker';
import FormModal from 'src/front/components//FormModal';

import {
  ButtonRow,
  CheckboxContainer,
  FormElement,
  FormRow,
  LineFormElement,
  ResetButton,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import { MODAL_VIEW } from 'src/front/constant';

import { useCardTypeState } from 'src/front/contexts/CardEntryContext';
import styled from 'styled-components';
import SuggestionDropdown from 'src/front/components/SuggestionDropdown';

export const REGISTRATION_ADDRESS_CODE = 'RA';
export const ACTUAL_ADDRESS_CODE = 'AL';
export const WORK_ADDRESS_CODE = 'WA';

const LongerFormElement = styled(FormElement)`
  width: 400px;
`;

type SelectedSuggestion = {
  country: Option | null;
  region: Option | null;
  city: string;
  district: Option | null;
  streetType: Option | null;
  street: string;
  houseNumber: string;
  buildingInfo: string;
  zipCode: string;
  flatNumber: string;
};

export interface AddressFormData {
  addressType: Option | null;
  isRegistration: boolean;
  addressQuery: string;
  manualAddress: boolean;
  registrationDate: Date | null;
  selectedSuggestion: SelectedSuggestion | null;

  country: Option | null;
  region: Option | null;
  city: string;
  district: Option | null;
  streetType: Option | null;
  street: string;
  buildingInfo: string;
  houseNumber: string;
  flatNumber: string;
  zipCode: string;
  addressId?: string;
  isAddressNew: boolean;
}

const defaultValues = {
  addressType: null,
  isRegistration: false,
  addressQuery: '',
  manualAddress: false,
  registrationDate: null,
  selectedSuggestion: null,

  country: null,
  region: null,
  city: '',
  district: null,
  streetType: null,
  street: '',
  buildingInfo: '',
  houseNumber: '',
  flatNumber: '',
  zipCode: '',
  isAddressNew: false,
};

interface Props {
  onClose: () => void;
  submit: (data: AddressFormData, secondAddress?: AddressFormData) => void;
  formValues?: AddressFormData;
  addressInfo: {
    hasRegistrationAddress: boolean;
    hasActualAddress: boolean;
  };
  addressTypes: Option[];
}

const AddressModal: FC<Props> = ({ formValues, onClose, submit, addressInfo, addressTypes }) => {
  const { t } = useTranslation();
  const isCreditCard = useCardTypeState() === CardSelectionType.CREDITCARD;
  const resolver = useYupResolver(
    AddressSchema,
    {
      todayMaxControl: true,
    },
    { isCreditCard }
  );

  const [suggestions, setSuggestions] = useState<FiasAddressSuggestion[]>([]);
  const [notFound, setNotFound] = useState<boolean>();
  const [addressDistrict, setAddressDistrict] = useState<string>('');

  const countries = usePRM(ReferenceDataType.COUNTRIES);
  const regions = usePRM(ReferenceDataType.REGION);
  const streetTypes = usePRM(ReferenceDataType.STREETTYPE);

  const { watch, register, errors, setValue, handleSubmit, formState, control, reset } = useForm<
    AddressFormData
  >({
    defaultValues: formValues || defaultValues,
    resolver,
  });
  const districts = useConditionalPRM(ReferenceDataType.DISTRICTTYPE, watch('region')?.value);

  const addressQuery = watch('addressQuery');

  const fetchAddressSuggestions = useCallback(async () => {
    const { data } = await Fias.searchAddress({ query: addressQuery });
    setSuggestions(data.suggestions || []);
  }, [addressQuery]);

  const debouncedSetSuggestions = useDebounce(fetchAddressSuggestions);

  useEffect(() => {
    debouncedSetSuggestions();
  }, [debouncedSetSuggestions, fetchAddressSuggestions]);

  useEffect(() => {
    if (addressTypes.length > 1 && watch('addressType') === null && !formValues) {
      const { hasRegistrationAddress, hasActualAddress } = addressInfo;

      if (!hasRegistrationAddress) {
        setValue(
          'addressType',
          addressTypes.find((address) => address.value === REGISTRATION_ADDRESS_CODE) || null
        );
      } else if (hasRegistrationAddress && !hasActualAddress) {
        setValue(
          'addressType',
          addressTypes.find((address) => address.value === ACTUAL_ADDRESS_CODE) || null
        );
      }
    }
  }, [addressInfo, addressTypes, formValues, setValue, watch]);

  const registerFields = useCallback(() => {
    register({ name: 'registrationDate' });
    register({ name: 'isRegistration' });
    register({ name: 'manualAddress' });
    register({ name: 'addressQuery' });
    register({ name: 'selectedSuggestion' });
    register({ name: 'country' });
    register({ name: 'region' });
    register({ name: 'city' });
    register({ name: 'district' });
    register({ name: 'streetType' });
    register({ name: 'street' });
    register({ name: 'buildingInfo' });
    register({ name: 'houseNumber' });
    register({ name: 'flatNumber' });
    register({ name: 'zipCode' });
  }, [register]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  useEffect(() => {
    if (districts.length && addressDistrict) {
      const district = districts.find((dist) => dist.label === addressDistrict) || null;
      setValue('district', district);
    }
  }, [addressDistrict, districts, setValue]);

  const handleDateChange = (date: Date): void =>
    setValue('registrationDate', date, {
      shouldValidate: formState.isSubmitted || !!date,
      shouldDirty: formState.isSubmitted || !!date,
    });

  const handleQueryChange = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('addressQuery', target.value);
  };
  const selectQuery = async (result: FiasAddressSuggestion): Promise<void> => {
    /* eslint-disable @typescript-eslint/camelcase */
    const {
      country_iso_code,
      postal_code,
      city,
      street_type_full,
      street,
      house,
      block,
      region_kladr_id,
      settlement_with_type,
      flat,
      area_with_type,
    } = result.data || {};

    let region = null;
    if (region_kladr_id) {
      const { data } = await Customer.getCustomerAddressRegionCodeByRfCode(
        region_kladr_id.slice(0, 2)
      );
      region = regions.find((reg) => reg.value === data.regionCode) || null;
    }

    const country = countries.find((count) => count.value === country_iso_code) || null;
    const streetType = streetTypes.find((type) => type.label === street_type_full) || null;
    // useConditionalPRM returns empty array before registering region.
    // so we need to postpone registering district
    setAddressDistrict(area_with_type || '');

    [
      { name: 'manualAddress', value: true },
      { name: 'addressQuery', value: result.value },
      { name: 'country', value: country },
      { name: 'region', value: region },
      {
        name: 'city',
        value: `${city ? `${city} ` : ''}${settlement_with_type || ''}`,
      },
      { name: 'streetType', value: streetType },
      { name: 'street', value: street || '' },
      { name: 'houseNumber', value: house || '' },
      { name: 'buildingInfo', value: block || '' },
      { name: 'zipCode', value: postal_code || '' },
      { name: 'flatNumber', value: flat || '' },
      {
        name: 'selectedSuggestion',
        value: {
          country,
          region,
          city: `${city ? `${city} ` : ''}${settlement_with_type || ''}`,
          streetType,
          street: street || '',
          houseNumber: house || '',
          buildingInfo: block || '',
          zipCode: postal_code || '',
          flatNumber: flat || '',
        },
      },
    ].forEach(({ name, value }) =>
      setValue(name, value, {
        shouldValidate: formState.isSubmitted,
        shouldDirty: formState.isSubmitted,
      })
    );
    /* eslint-enable @typescript-eslint/camelcase */
  };

  const onReset = (): void => {
    reset();
    registerFields();
    setNotFound(undefined);
    setAddressDistrict('');
  };
  const onSubmit = async (data: AddressFormData): Promise<void> => {
    if (data.isRegistration) {
      await submit(
        { ...formValues, ...data },
        {
          ...formValues,
          ...data,
          isRegistration: false,
          addressType:
            addressTypes.find((address) => address.value === ACTUAL_ADDRESS_CODE) || null,
          ...(!isCreditCard ? { registrationDate: null } : {}),
        }
      );
    } else {
      await submit({ ...formValues, ...data });
    }
  };

  const handleComboChange = (name: string) => (option: Option | null) => {
    setValue(name, option, { shouldValidate: true, shouldDirty: true });
  };
  const handleTextChange = (name: string) => (value: string) => {
    setValue(name, value || '');
  };

  const handleNotFound = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setNotFound(target.checked);
    setValue('manualAddress', target.checked);
  };

  const handleSameAddress = ({ target }: ChangeEvent<HTMLInputElement>): void => {
    setValue('isRegistration', target.checked);
  };

  return (
    <FormModal
      onClose={onClose}
      size={watch('manualAddress') ? MODAL_VIEW.BIG : MODAL_VIEW.MIDDLE}
      zIndex={900}
    >
      <Title>
        <Trans i18nKey="cardEntry_addressEntry_title" />
      </Title>
      <Subtitle>
        <Trans i18nKey="cardEntry_addressEntry_addressList_text" />
      </Subtitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <FormElement>
            <Controller
              control={control}
              name="addressType"
              render={({ onChange, value }) => (
                <Combobox
                  errorMessage={t((errors?.addressType as FieldError)?.message as string)}
                  items={addressTypes}
                  label={t('cardEntry_addressEntry_addressType_label')}
                  name="addressType"
                  onChange={onChange}
                  searchPlaceholder={t('cardEntry_documentEntry_searchAddressType_text')}
                  value={value}
                  required
                  searchable
                />
              )}
            />
          </FormElement>
          {!formValues &&
          watch('addressType')?.value === REGISTRATION_ADDRESS_CODE &&
          !addressInfo.hasActualAddress ? (
            <CheckboxContainer>
              <Checkbox
                id="isRegistration"
                label={t('cardEntry_addressEntry_sameWithRegistration_label')}
                onChange={handleSameAddress}
                value={watch('isRegistration')}
              />
            </CheckboxContainer>
          ) : null}
        </FormRow>
        <FormRow>
          <LineFormElement>
            <SuggestionDropdown
              dropdownHeight={watch('manualAddress') ? 300 : 210}
              errorMessage={t(errors?.addressQuery?.message as string)}
              items={suggestions}
              label={t('cardEntry_addressEntry_fiasAddressSearch_label')}
              name="addressQuery"
              onChange={handleQueryChange}
              onItemSelect={selectQuery}
              value={watch('addressQuery')}
              required
            />
          </LineFormElement>
        </FormRow>
        <FormRow>
          {!watch('selectedSuggestion') && watch('addressQuery') ? (
            <Checkbox
              id="manualAddress"
              label={t('cardEntry_addressEntry_manuelAddressEntry_label')}
              onChange={handleNotFound}
              value={notFound}
            />
          ) : null}
        </FormRow>
        {watch('manualAddress') ? (
          <>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={!!watch('selectedSuggestion')?.country}
                  errorMessage={t((errors?.country as FieldError)?.message as string)}
                  items={countries}
                  label={t('general_country_label')}
                  name="country"
                  onChange={handleComboChange('country')}
                  searchPlaceholder={t('cardEntry_addressEntry_searchCountry_text')}
                  value={watch('country')}
                  required
                  searchable
                />
              </FormElement>
              <FormElement>
                <Combobox
                  disabled={!!watch('selectedSuggestion')?.region}
                  errorMessage={t((errors?.region as FieldError)?.message as string)}
                  items={regions}
                  label={t('cardEntry_addressEntry_area_label')}
                  name="region"
                  onChange={handleComboChange('region')}
                  searchPlaceholder={t('cardEntry_addressEntry_searchRegion_text')}
                  value={watch('region')}
                  required
                  searchable
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={!!watch('selectedSuggestion')?.city}
                  errorMessage={t((errors?.city as FieldError)?.message as string)}
                  formatter={formatFreeText}
                  label={t('general_city_text')}
                  maxLength={24}
                  name="city"
                  onChange={handleTextChange('city')}
                  value={watch('city')}
                  required
                />
              </FormElement>
              <FormElement>
                <Combobox
                  errorMessage={t((errors?.district as FieldError)?.message as string)}
                  items={districts}
                  label={t('cardEntry_addressEntry_district_label')}
                  name="district"
                  onChange={handleComboChange('district')}
                  searchPlaceholder={t('cardEntry_addressEntry_searchDistrict_text')}
                  value={watch('district')}
                  searchable
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={!!watch('selectedSuggestion')?.streetType}
                  errorMessage={t((errors?.streetType as FieldError)?.message as string)}
                  items={streetTypes}
                  label={t('cardEntry_addressEntry_streetType_label')}
                  name="streetType"
                  onChange={handleComboChange('streetType')}
                  searchPlaceholder={t('cardEntry_addressEntry_searchStreetType_text')}
                  value={watch('streetType')}
                  required
                  searchable
                />
              </FormElement>
              <FormElement>
                <TextInput
                  disabled={!!watch('selectedSuggestion')?.street}
                  errorMessage={t((errors?.street as FieldError)?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_addressEntry_street_label')}
                  maxLength={60}
                  name="street"
                  onChange={handleTextChange('street')}
                  required={watch('addressType')?.value === WORK_ADDRESS_CODE}
                  value={watch('street')}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={!!watch('selectedSuggestion')?.houseNumber}
                  errorMessage={t((errors?.houseNumber as FieldError)?.message as string)}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_addressEntry_house_label')}
                  maxLength={10}
                  name="houseNumber"
                  onChange={handleTextChange('houseNumber')}
                  required={watch('addressType')?.value === WORK_ADDRESS_CODE}
                  value={watch('houseNumber')}
                />
              </FormElement>
              <FormElement>
                <TextInput
                  disabled={!!watch('selectedSuggestion')?.buildingInfo}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_addressEntry_housing_label')}
                  maxLength={10}
                  name="buildingInfo"
                  onChange={handleTextChange('buildingInfo')}
                  value={watch('buildingInfo')}
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <TextInput
                  disabled={!!watch('selectedSuggestion')?.flatNumber}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_addressEntry_flat_label')}
                  maxLength={7}
                  name="flatNumber"
                  onChange={handleTextChange('flatNumber')}
                  value={watch('flatNumber')}
                />
              </FormElement>
              <FormElement>
                <TextInput
                  disabled={!!watch('selectedSuggestion')?.zipCode}
                  formatter={formatAlphaNumericCyrillicPlusDashAndBlank}
                  label={t('cardEntry_addressEntry_zipCode_label')}
                  maxLength={7}
                  name="zipCode"
                  onChange={handleTextChange('zipCode')}
                  value={watch('zipCode')}
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}

        {watch('addressType')?.value === REGISTRATION_ADDRESS_CODE ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_addressEntry_otherDetails_subtitle" />
            </Subtitle>
            <FormRow>
              <LongerFormElement>
                <Datepicker
                  errorMessage={t(errors?.registrationDate?.message as string)}
                  inputLabel={`${t('cardEntry_addressEntry_registrationDate_label')} *`}
                  onChange={handleDateChange}
                  value={watch('registrationDate') as Date}
                />
              </LongerFormElement>
            </FormRow>
          </>
        ) : null}
        {watch('addressType')?.value === ACTUAL_ADDRESS_CODE && isCreditCard ? (
          <>
            <Subtitle>
              <Trans i18nKey="cardEntry_addressEntry_otherDetails_subtitle" />
            </Subtitle>
            <FormRow>
              <LongerFormElement>
                <Datepicker
                  errorMessage={t(errors?.registrationDate?.message as string)}
                  inputLabel={`${t('cardEntry_addressEntry_livingStartDate_label')} *`}
                  onChange={handleDateChange}
                  value={watch('registrationDate') as Date}
                />
              </LongerFormElement>
            </FormRow>
          </>
        ) : null}
        <ButtonRow>
          {formState.isDirty ? (
            <ResetButton
              color={ButtonColor.Secondary}
              onClick={onReset}
              size={ButtonSize.Medium}
              text={t('general_components_reset_button')}
              type="reset"
            />
          ) : null}
          <Button
            color={ButtonColor.Primary}
            disabled={formState.isSubmitting}
            size={ButtonSize.Medium}
            text={t('general_save_button')}
            type="submit"
          />
        </ButtonRow>
      </form>
    </FormModal>
  );
};

export default AddressModal;
